import { useEffect, useState } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { getShiftsPage, GetShiftsPageOutputType } from 'zite-endpoints-sdk';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Clock, CalendarDays, AlertCircle, CheckCircle2, Users, Wifi, WifiOff, MapPin } from 'lucide-react';
import { format, parseISO, isToday, isTomorrow, isPast, formatDistanceToNow } from 'date-fns';
import { isManagerPlus } from '../lib/roles';

type Shift = GetShiftsPageOutputType['shifts'][0];
type MatchStatus = GetShiftsPageOutputType['plandayMatchStatus'];

function fmtDate(d: string) { try { return format(parseISO(d), 'EEE dd MMM'); } catch { return d; } }
function dayLabel(d: string) {
  try {
    const dt = parseISO(d);
    if (isToday(dt)) return 'Today';
    if (isTomorrow(dt)) return 'Tomorrow';
    return format(dt, 'EEEE');
  } catch { return d; }
}

function groupByDate(shifts: Shift[]) {
  const groups: Record<string, Shift[]> = {};
  shifts.forEach(s => {
    const key = s.date;
    if (!groups[key]) groups[key] = [];
    groups[key].push(s);
  });
  return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
}

function ShiftCard({ shift, showName }: { shift: Shift; showName?: boolean }) {
  const pastShift = shift.date ? isPast(parseISO(`${shift.date}T${shift.endTime || '23:59'}`)) : false;

  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg border border-border transition-colors ${pastShift ? 'opacity-50 bg-muted/20' : 'bg-card'}`}>
      <div className="flex flex-col items-center justify-center w-14 shrink-0">
        <span className="text-lg font-bold text-foreground leading-tight">{shift.startTime || '—'}</span>
        <span className="text-[10px] text-muted-foreground">to {shift.endTime || '—'}</span>
      </div>
      <div className="h-8 w-px bg-border shrink-0" />
      <div className="flex-1 min-w-0">
        {showName && shift.employeeName && (
          <p className="text-sm font-semibold text-foreground truncate">{shift.employeeName}</p>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          {shift.positionName && <Badge variant="secondary" className="text-xs">{shift.positionName}</Badge>}
          {shift.departmentName && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <MapPin className="w-3 h-3" />{shift.departmentName}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function RotaFreshness({ data }: { data: GetShiftsPageOutputType['rotaFreshness'] }) {
  return (
    <div className={`flex items-center gap-2 text-xs px-3 py-2 rounded-lg ${data.plandayReachable ? 'bg-primary/5 text-primary' : 'bg-destructive/10 text-destructive'}`}>
      {data.plandayReachable ? <Wifi className="w-3.5 h-3.5" /> : <WifiOff className="w-3.5 h-3.5" />}
      <span>
        {data.plandayReachable ? 'Planday connected' : 'Planday offline'} · {data.shiftCount} shift{data.shiftCount !== 1 ? 's' : ''} loaded · refreshed {formatDistanceToNow(parseISO(data.lastFetchedAt), { addSuffix: true })}
      </span>
    </div>
  );
}

function UnmatchedQueue({ match }: { match: MatchStatus }) {
  if (!match || match.unmatched.length === 0) return null;

  return (
    <Card>
      <CardContent className="pt-5 pb-4">
        <div className="flex items-center gap-2 mb-3">
          <AlertCircle className="w-4 h-4 text-amber-500" />
          <h3 className="text-sm font-semibold text-foreground">Unmatched Staff ({match.unmatched.length})</h3>
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          These staff members could not be matched to a Planday employee. Set their Planday ID in the Admin Panel → Employees drawer to resolve.
        </p>
        <div className="divide-y divide-border border border-border rounded-lg overflow-hidden">
          {match.unmatched.map(u => (
            <div key={u.staffId} className="flex items-center justify-between p-2.5">
              <div>
                <p className="text-sm font-medium text-foreground">{u.displayName}</p>
                <p className="text-xs text-muted-foreground">{u.email}</p>
              </div>
              <Badge variant="secondary" className="text-xs shrink-0">{u.reason}</Badge>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default function MyShifts() {
  const { user } = useAuth();
  const [mode, setMode] = useState<'my' | 'team'>('my');
  const [data, setData] = useState<GetShiftsPageOutputType | null>(null);
  const [loading, setLoading] = useState(true);
  const canSeeTeam = isManagerPlus(user?.role);

  const load = (m: 'my' | 'team') => {
    setLoading(true);
    getShiftsPage({ mode: m, weeksAhead: 2 })
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!user) return;
    load(mode);
  }, [user, mode]);

  if (loading) return (
    <div className="container mx-auto px-4 py-6 max-w-3xl space-y-4">
      <Skeleton className="h-10 w-48" />
      <Skeleton className="h-10 w-64" />
      {[1, 2, 3].map(i => <Skeleton key={i} className="h-20" />)}
    </div>
  );

  const shifts = data?.shifts || [];
  const grouped = groupByDate(shifts);

  return (
    <div className="container mx-auto px-4 py-6 max-w-3xl space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <CalendarDays className="w-6 h-6 text-primary" />
          <div>
            <h1 className="text-xl font-bold text-foreground">{mode === 'my' ? 'My Shifts' : 'Team Shifts'}</h1>
            <p className="text-sm text-muted-foreground">Next 2 weeks from Planday · Read-only</p>
          </div>
        </div>
        {canSeeTeam && (
          <Tabs value={mode} onValueChange={v => setMode(v as 'my' | 'team')}>
            <TabsList>
              <TabsTrigger value="my"><Clock className="w-3.5 h-3.5 mr-1.5" />My Shifts</TabsTrigger>
              <TabsTrigger value="team"><Users className="w-3.5 h-3.5 mr-1.5" />Team</TabsTrigger>
            </TabsList>
          </Tabs>
        )}
      </div>

      {/* Rota Freshness */}
      {data?.rotaFreshness && <RotaFreshness data={data.rotaFreshness} />}

      {/* Error */}
      {data?.error && (
        <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-destructive shrink-0" />
          <p className="text-sm text-destructive">{data.error}</p>
        </div>
      )}

      {/* Match Status Summary (team mode) */}
      {mode === 'team' && data?.plandayMatchStatus && (
        <div className="grid grid-cols-3 gap-3">
          <Card>
            <CardContent className="pt-4 pb-3 text-center">
              <p className="text-2xl font-bold text-foreground">{data.plandayMatchStatus.totalStaff}</p>
              <p className="text-xs text-muted-foreground">Total Staff</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3 text-center">
              <p className="text-2xl font-bold text-primary">
                {data.plandayMatchStatus.matchedByEmail + data.plandayMatchStatus.matchedByPlandayId}
              </p>
              <p className="text-xs text-muted-foreground">Matched</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3 text-center">
              <p className={`text-2xl font-bold ${data.plandayMatchStatus.unmatched.length > 0 ? 'text-amber-500' : 'text-primary'}`}>
                {data.plandayMatchStatus.unmatched.length}
              </p>
              <p className="text-xs text-muted-foreground">Unmatched</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Shifts */}
      {shifts.length === 0 && !data?.error ? (
        <Card>
          <CardContent className="py-12 text-center">
            <CheckCircle2 className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">
              {mode === 'my' ? 'No upcoming shifts found in Planday.' : 'No team shifts found for this period.'}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {mode === 'my' ? 'Your email may not match a Planday employee, or no shifts are scheduled.' : 'Check that staff have Planday IDs set.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-5">
          {grouped.map(([date, dayShifts]) => (
            <div key={date}>
              <div className="flex items-center gap-2 mb-2">
                <h2 className="text-sm font-semibold text-foreground">{dayLabel(date)}</h2>
                <span className="text-xs text-muted-foreground">{fmtDate(date)}</span>
                <Badge variant="secondary" className="text-xs ml-auto">{dayShifts.length} shift{dayShifts.length !== 1 ? 's' : ''}</Badge>
              </div>
              <div className="space-y-2">
                {dayShifts.map(s => (
                  <ShiftCard key={s.id} shift={s} showName={mode === 'team'} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Unmatched Queue (team mode) */}
      {mode === 'team' && data?.plandayMatchStatus && <UnmatchedQueue match={data.plandayMatchStatus} />}

      {/* Safety note */}
      <p className="text-xs text-muted-foreground text-center pt-2">
        Read-only feed from Planday. No data is written back. No pay or private HR data included.
      </p>
    </div>
  );
}
